import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { CATEGORIES } from "@/data/site";
import { track } from "@/lib/analytics";
import { useDemoGate } from "@/components/DemoGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [...CATEGORIES.map((c) => ({ value: c.slug, label: c.name })), { value: "other", label: "Something else" }];

export default function InquiryForm({ defaultService = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: defaultService, preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { gate } = useDemoGate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    gate(async () => {
      setLoading(true);
      try {
        await axios.post(`${API}/inquiries`, form);
        track("contact_form_submit", { service: form.service || "unspecified" });
        toast.success("Thank you — we'll be in touch within one business day.");
        setForm({ name: "", email: "", phone: "", service: defaultService, preferred_time: "", message: "" });
      } catch (err) {
        toast.error("Something went wrong. Please call or text us instead.");
      } finally {
        setLoading(false);
      }
    });
  };

  const field = "w-full border-b border-border bg-transparent py-3 font-sans text-foreground placeholder:text-muted-foreground/60 focus:border-wine focus:outline-none transition-colors";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="inquiry-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className="label mb-2 block" htmlFor="name">Name *</label>
          <input id="name" data-testid="inquiry-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className="label mb-2 block" htmlFor="email">Email *</label>
          <input id="email" type="email" data-testid="inquiry-email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
        <div>
          <label className="label mb-2 block" htmlFor="phone">Phone</label>
          <input id="phone" data-testid="inquiry-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(212) 555-0000" />
        </div>
        <div>
          <label className="label mb-2 block" htmlFor="service">Service</label>
          <select id="service" data-testid="inquiry-service" className={`${field} appearance-none`} value={form.service} onChange={set("service")}>
            <option value="">Select a studio…</option>
            {SERVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-background">{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="label mb-2 block" htmlFor="preferred_time">Preferred day / time</label>
        <input id="preferred_time" data-testid="inquiry-time" className={field} value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Saturday morning" />
      </div>
      <div>
        <label className="label mb-2 block" htmlFor="message">How can we help? *</label>
        <textarea id="message" data-testid="inquiry-message" rows={4} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us what you're looking for…" />
      </div>
      <button
        type="submit"
        data-testid="inquiry-submit"
        disabled={loading}
        className="group inline-flex items-center gap-2 bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={1.5} />}
        {loading ? "Sending…" : "Send inquiry"}
      </button>
    </form>
  );
}
